import { createContext, useContext } from "react";
import type { MutableRefObject } from "react";

/**
 * Module-singleton scroll container ref.
 *
 * This was a `signal`, but `scrollRef.value` is assigned nowhere in the repo — only
 * `scrollRef.value.current` is mutated (routes.tsx). A signal notifies on `.value =`
 * only, so it never fired a single subscription; a plain ref object is exactly
 * equivalent. See ScrollContainerContext below for the per-tab alternative.
 */
export const scrollRef: MutableRefObject<HTMLDivElement | null> = { current: null };

/**
 * Per-instance scroll-container ref, scoped via React context rather than the module-level
 * `scrollRef` above. Under native navigation, Home/Chat/Team are separate portaled
 * `AppRoutes` instances that all share the same `scrollRef` (one JS realm): a
 * mount/unmount effect in one tab can end up reading or writing another tab's DOM node if
 * it fires while `scrollRef.current` happens to point at that other tab's node.
 * Consumers that imperatively mutate "the current tab's own scroll container" (safe-area
 * grid-row toggling) should use this context instead, so they can never affect a different
 * tab's node.
 */
export const ScrollContainerContext = createContext<MutableRefObject<HTMLDivElement | null> | null>(null);

export function useScrollContainerRef(): MutableRefObject<HTMLDivElement | null> {
  const ref = useContext(ScrollContainerContext);
  if (!ref) throw new Error("useScrollContainerRef must be used within ScrollContainerContext.Provider");
  return ref;
}
